// Copyright (c) StrangeBee 2023

import { useLocalMessage } from '@Helpers/locale';
import Upload from 'antd/lib/upload';
import { useState } from 'react';
import { FormInstance, Space } from 'antd';
import { PEMCertificateInput } from 'thehive-sdk';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { AlertImport } from '@Molecules/AlertImport/AlertImport';
import { ICONS } from '@Constants/iconsApp';
import { UploadFile } from 'antd/lib/upload/interface';

interface CertificateImportProps {
    name: string[];
    form: FormInstance;
    visible: boolean;
    onVisibleChange: (visible: boolean) => void;
    add: (defaultValue?: any, insertIndex?: number | undefined) => void;
}

export const CertificateImport = ({ name, form, visible, onVisibleChange, add }: CertificateImportProps) => {
    const i = useLocalMessage();

    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const splitCertificates = (pem: string): string[] => {
        const matches = pem.matchAll(/-----BEGIN CERTIFICATE-----([\s\S]*?)-----END CERTIFICATE-----/g);
        return Array.from(matches, (base64) => '-----BEGIN CERTIFICATE-----' + base64[1] + '-----END CERTIFICATE-----');
    };

    const formatCertificates = (certificates: string[]): PEMCertificateInput[] => {
        return certificates.map((cert: string) => {
            return {
                type: 'PEM',
                data: cert,
            };
        });
    };

    const onFinish = () => {
        fileList.forEach((file: UploadFile) => {
            const reader = new FileReader();
            const blob = file.originFileObj;

            reader.onload = (): void => {
                const data = reader.result;
                const certificateList = formatCertificates(splitCertificates(data as string));
                const currentCertificates: PEMCertificateInput[] | undefined = form.getFieldValue(name);

                const alreadyExists = (newCert: PEMCertificateInput) =>
                    currentCertificates?.find((currentCert) => {
                        return newCert.data === currentCert.data;
                    });

                certificateList.forEach((newCert: PEMCertificateInput) => {
                    if (!alreadyExists(newCert)) {
                        add(newCert);
                    }
                });
            };
            blob && reader.readAsText(blob);
        });
    };

    const beforeUpload = () => {
        return false;
    };

    return (
        <THDrawer
            title={i('sslConfig.field.certificateAuthorities.add')}
            className="drawer-form"
            visible={visible}
            afterVisibleChange={(visible) => {
                onVisibleChange(visible);
            }}
            disableClose={fileList.length !== 0}
            width="40vw"
        >
            {(toggleDrawer) => (
                <THForm.Layout
                    footer={
                        <FormSubmitButton
                            onCancel={() => {
                                toggleDrawer();
                            }}
                            onSubmit={() => {
                                toggleDrawer();
                                onFinish();
                            }}
                            disabled={fileList.length === 0}
                        />
                    }
                >
                    <Space direction="vertical" className="file-import__container" size="large">
                        <AlertImport title={i('sslConfig.field.certificateAuthorities.add')}>
                            <>
                                <p className="import-alert__content">
                                    {i('sslConfig.field.certificateAuthorities.drawer.description')}
                                </p>
                                <p className="import-alert__content">
                                    {i('sslConfig.field.certificateAuthorities.drawer.format')}
                                </p>
                            </>
                        </AlertImport>
                        <Space direction="vertical" className="file-import__upload ">
                            <Upload.Dragger
                                accept=".pem"
                                beforeUpload={beforeUpload}
                                multiple
                                fileList={fileList}
                                className="attachments-create__upload-list"
                                onChange={({ fileList }) => {
                                    setFileList(fileList);
                                }}
                                showUploadList={{
                                    showDownloadIcon: false,
                                    showRemoveIcon: true,
                                    removeIcon: <ICONS.DeleteIcon />,
                                }}
                            >
                                {i('general.import.upload.content')}
                            </Upload.Dragger>
                            <span>{i('sslConfig.field.certificateAuthorities.drawer.instruction')}</span>
                        </Space>
                    </Space>
                </THForm.Layout>
            )}
        </THDrawer>
    );
};
